import {
  CommonFilterList,
  FilterConfig,
  FilterType,
  SegmentFilterList,
} from 'common/models/filter.model';
import { Claim, Feature, FilterAttributeType } from 'common/models/page-config.models';

type Filters =
  | 'alignmentChange'
  | 'category'
  | 'categoryStatus'
  | 'channel'
  | 'duration'
  | 'dateRange'
  | 'fieldTeam'
  | 'interactionType'
  | 'managerName'
  | 'product'
  | 'profile'
  | 'question'
  | 'response'
  | 'sentiment'
  | 'specialty'
  | 'surveyQuestionResponse'
  | 'tags'
  | 'team'
  | 'teamMember'
  | 'insightSourceName'
  | 'territory'
  | 'topic'
  | 'alignmentScore'
  | 'previousAlignment'
  | 'accountCountry'
  | 'accountCountryAbbreviation'
  | 'keyword'
  | 'diseaseState'
  | 'treatment'
  | 'total'
  | 'planningDateRange';

export const UNIVERSAL_FILTER_ATTRIBUTE_TYPES: FilterAttributeType[] = [
  'AccountAttribute',
  'ProductAttribute',
  'ProfileAttribute',
  'TeamAttribute',
  'UserAttribute',
  'AccountHierarchyAttribute',
];

type FilterItems = {
  [key in Filters]: FilterConfig;
};

export const FILTER_ITEMS: FilterItems = {
  alignmentChange: {
    property: CommonFilterList.AlignmentChange,
    type: FilterType.AUTOCOMPLETE,
    label: 'Alignment Change',
    claims: [Claim.Alignment],
    features: [],
  },
  category: {
    property: CommonFilterList.Survey,
    type: FilterType.AUTOCOMPLETE_TREE,
    label: 'Category',
    claims: [Claim.Insights, Claim.Alignment],
    features: [],
  },
  categoryStatus: {
    property: CommonFilterList.CategoryStatus,
    type: FilterType.AUTOCOMPLETE,
    label: 'Category Status',
    claims: [Claim.Insights, Claim.Alignment],
    features: [],
  },
  channel: {
    property: CommonFilterList.Channel,
    type: FilterType.AUTOCOMPLETE,
    label: 'Channel',
    claims: [Claim.Interaction],
    features: [],
  },

  dateRange: {
    property: '',
    type: FilterType.DATE,
    label: 'Date Range',
    claims: [],
    features: [],
  },
  planningDateRange: {
    property: '',
    type: FilterType.PLANNING_DATE,
    label: 'Planning Date Range',
    claims: [],
    features: [],
  },
  fieldTeam: {
    property: CommonFilterList.FieldTeam,
    type: FilterType.AUTOCOMPLETE,
    label: 'Field Team',
    claims: [],
    features: [],
  },
  interactionType: {
    property: CommonFilterList.InteractionType,
    type: FilterType.AUTOCOMPLETE,
    label: 'Interaction Type',
    claims: [Claim.Interaction],
    features: [],
  },
  managerName: {
    property: CommonFilterList.ManagerName,
    type: FilterType.AUTOCOMPLETE,
    label: 'Manager Name',
    claims: [],
    features: [],
  },
  product: {
    property: CommonFilterList.Product,
    type: FilterType.AUTOCOMPLETE,
    label: 'Product',
    claims: [],
    features: [],
  },
  profile: {
    property: CommonFilterList.Profile,
    type: FilterType.AUTOCOMPLETE,
    label: 'Profile',
    claims: [],
    features: [],
  },
  question: {
    property: CommonFilterList.Question,
    type: FilterType.AUTOCOMPLETE,
    label: 'Question',
    claims: [Claim.Insights, Claim.Alignment],
    features: [],
  },
  response: {
    property: CommonFilterList.Response,
    type: FilterType.AUTOCOMPLETE,
    label: 'Response',
    claims: [Claim.Insights, Claim.Alignment],
    features: [],
  },
  specialty: {
    property: CommonFilterList.Specialty,
    type: FilterType.AUTOCOMPLETE,
    label: 'Specialty',
    claims: [],
    features: [],
  },
  surveyQuestionResponse: {
    property: CommonFilterList.Survey,
    type: FilterType.AUTOCOMPLETE_TREE,
    label: 'Category/Question/Response',
    claims: [Claim.Insights, Claim.Alignment],
    features: [],
  },
  tags: {
    property: CommonFilterList.Tags,
    type: FilterType.AUTOCOMPLETE,
    label: 'Tags',
    claims: [],
    features: [],
  },
  team: {
    property: CommonFilterList.Team,
    type: FilterType.AUTOCOMPLETE,
    label: 'Team',
    claims: [Claim.Insights],
    features: [],
  },
  teamMember: {
    property: CommonFilterList.FieldTeam,
    type: FilterType.AUTOCOMPLETE,
    label: 'Team Member',
    claims: [],
    features: [],
  },
  insightSourceName: {
    property: CommonFilterList.InsightSourceName,
    type: FilterType.AUTOCOMPLETE,
    label: 'Insight Source',
    claims: [],
    features: [],
  },
  territory: {
    property: CommonFilterList.Territory,
    type: FilterType.AUTOCOMPLETE,
    label: 'Territory',
    claims: [],
    features: [],
  },
  topic: {
    property: CommonFilterList.Topic,
    type: FilterType.AUTOCOMPLETE,
    label: 'Topic',
    claims: [Claim.Interaction],
    features: [],
  },
  sentiment: {
    property: CommonFilterList.Sentiment,
    type: FilterType.AUTOCOMPLETE,
    label: 'Sentiment',
    claims: [],
    features: [Feature.Sentiments],
  },
  previousAlignment: {
    property: CommonFilterList.PreviousAlignment,
    type: FilterType.AUTOCOMPLETE,
    label: 'Previous Alignment',
    claims: [Claim.Alignment],
    features: [Feature.AlignmentPreviousLatestFilterSankey],
  },
  alignmentScore: {
    property: CommonFilterList.AlignmentScore,
    type: FilterType.AUTOCOMPLETE,
    label: 'Latest Alignment',
    claims: [Claim.Alignment],
    features: [Feature.AlignmentPreviousLatestFilterSankey],
  },
  accountCountry: {
    property: CommonFilterList.Country,
    type: FilterType.AUTOCOMPLETE,
    label: 'Country',
    claims: [],
    features: [Feature.FilterProfileCountry],
  },
  accountCountryAbbreviation: {
    property: SegmentFilterList.Country,
    type: FilterType.AUTOCOMPLETE,
    label: 'Country',
    claims: [],
    features: [Feature.FilterProfileCountry],
  },
  keyword: {
    property: SegmentFilterList.Keyword,
    type: FilterType.AUTOCOMPLETE,
    label: 'Keyword',
    claims: [],
    features: [],
  },
  duration: {
    property: SegmentFilterList.Duration,
    type: FilterType.AUTOCOMPLETE,
    label: 'Duration',
    claims: [],
    features: [],
  },
  diseaseState: {
    property: SegmentFilterList.DiseaseState,
    type: FilterType.AUTOCOMPLETE,
    label: 'Condition',
    claims: [],
    features: [],
  },
  treatment: {
    property: SegmentFilterList.Treatment,
    type: FilterType.AUTOCOMPLETE,
    label: 'Treatment',
    claims: [],
    features: [],
  },
  total: {
    property: SegmentFilterList.Total,
    type: FilterType.AUTOCOMPLETE,
    label: 'Total',
    claims: [],
    features: [],
  },
};

export const FILTER_ITEMS_KEYS = (Object.keys(FILTER_ITEMS) as Filters[]).map(
  (key) => FILTER_ITEMS[key].property
);

export const getFilterConfig = (filter: string): FilterConfig | undefined => {
  const keys = Object.keys(FILTER_ITEMS) as Filters[];
  const foundFilterKey = keys.find((key) => FILTER_ITEMS[key].property === filter);

  return foundFilterKey ? FILTER_ITEMS[foundFilterKey] : undefined;
};
